<script lang="ts" setup>

  import type { NavigationMenuItem } from '@nuxt/ui'

  // Currency Store
  const currencyStore = useCurrencyStore()
  const { currency } = storeToRefs(currencyStore)

  // Device
  const deviceStore = useDeviceStore()
  const isMobile = deviceStore.isMobile

  // Idioma
  const { locale, setLocaleCookie, t } = useI18n()

  const selectedIdioma = computed(() => locale.value)
  setLocaleCookie(locale.value)

  // Strapi
  const strapiData = await getStrapiData('global', {
    populate: [
      "header.logo",
      "header.destinos",
      "header.buttonDestinos",
      "header.buttonFunciona",
      "header.buttonPartner",
      "header.buttonAyuda",
      "header.buttonPerfil",
      "header.buttonTarjeta"
    ],
    locale: locale.value
  })
 const strapiContent = strapiData?.header

  // Moneda
  const { data: dataMonedas } = await useLazyFetch<{ data: Currency[] }>(`${useBaseUrl()}/currencies`, {
    key: 'currencies',
    headers: { "Content-Type": "application/json" },
    timeout: 5000, // esperar 5 segundos
    query: {
      locale
    }
  })

  // Menu
  const open = ref(false)
  const openFunciona = ref(false)
  const active = ref()

  const items = ref<NavigationMenuItem[]>([
    {
      label: strapiContent?.tituloDestinos,
      icon: 'i-lucide-map',
      slot: 'destinos',
      children: strapiContent?.destinos?.slice(0, 10)?.map((destino: any) => ({
        label: destino?.label,
        to: `/${locale.value}/${destino?.link}-${destino?.key}d.html`,
        onSelect: () => {
          open.value = false
        }
      })) || [],
    },
    ...(isMobile ? [{
      label: t('buttons.mapa'),
      icon: 'i-lucide-globe',
      to: getButtonUrl(strapiContent?.buttonDestinos),
      onSelect: () => {
      open.value = false
      }
    }] : []),
    {
      label: strapiContent?.buttonFunciona?.label,
      icon: 'i-lucide-chart-pie',
      to: `/${locale.value}/como-funciona.html`,
      open: openFunciona.value,
      onSelect: () => {
        open.value = false
      }
    },
    {
      label: strapiContent?.labelPartners,
      icon: 'i-lucide-message-circle',
      children: strapiContent?.buttonPartner?.map((opcion: StrapiButton) => ({
        label: opcion?.label,
        to: opcion?.link,
        target  : '_blank',
        onSelect: () => {
          open.value = false
        }
      }))
    },
    {
      label: strapiContent?.labelAyuda,
      icon: 'i-lucide-help-circle',
      children: strapiContent?.buttonAyuda?.map((opcion: StrapiButton) => ({
        label: opcion?.label,
        to: opcion?.link,
        target: '_blank',
        onSelect: () => {
          open.value = false
        }
      }))
    }
  ])

  // Scroll Store
  const scroll = useScrollStore()
  const { isScrolledInput } = storeToRefs(scroll)

  // Home Store
  const { isHome } = useIsHome()

  // User data
  const { user, clear } = useUserSession()
  const byhoursConnect = useCookie('byhours_connect')
  const menuItems = computed(() => [
    [
      {
        name: (user.value as OAuthUser)?.name,
        label: (user.value as OAuthUser)?.email,
        img: (user.value as OAuthUser)?.img,
        slot: 'account',
        onClick: () => {
          navigateTo(`/${locale.value}/user-zone/bookings`)
        }
      }
    ],
    [
      {
        label: t('labels.user_zone.menu.reservas'),
        icon: 'i-lucide-building-2',
        onClick: () => {
          navigateTo(`/${locale.value}/user-zone/bookings`)
        }
      },
      {
        label: t('labels.user_zone.menu.favoritos'),
        icon: 'i-heroicons-heart',
        onClick: () => {
          navigateTo(`/${locale.value}/user-zone/favorites`)
        }
      },
      {
        label: t('labels.user_zone.menu.perfil'),
        icon: 'i-lucide-user-circle',
        onClick: () => {
          navigateTo(`/${locale.value}/user-zone/profile`)
        }
      }
    ],
    [
      {
        label: t('labels.login.dropdown.cerrar'),
        icon: 'i-lucide-log-out',
        onClick: () => {
          clear()
          byhoursConnect.value = null
        }
      }
    ]
  ])

  function generateUsername(email: string): string {
    if (!email?.includes("@")) {
      return "";
    }
    
    const [localPart] = email.split("@");
    return localPart?.replace(/[^a-zA-Z0-9]/g, "_") || "";
  }

</script>

<template>
  <header class="top-0 left-0 right-0 w-full h-16 z-50 backdrop-blur-lg transition shadow-xs sticky bg-white/80">
    <div class="px-5 py-4 mx-auto flex justify-between items-center h-full" :class="`${ isHome ? 'max-w-screen-2xl' : 'max-w-screen-2xl'}`">
      <div class="flex items-center w-full">
        
        <!-- Logo -->
        <NuxtLink aria-label="Home" :to="`/${locale}`">
          <svg width="75" height="18" viewBox="0 0 80 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M36.1406 0.528589C35.7064 0.699016 35.2846 0.909387 34.8741 1.16236C33.6164 1.93594 32.6219 2.9878 31.8907 4.31792C31.1595 5.64805 30.7939 7.09535 30.7939 8.65848C30.7939 10.2216 31.1595 11.6689 31.8907 12.999C32.6219 14.3292 33.6164 15.381 34.8741 16.1546C36.1306 16.9282 37.4982 17.3156 38.9768 17.3156C40.4554 17.3156 41.823 16.9282 43.0795 16.1546C44.336 15.381 45.3305 14.3292 46.063 12.999C46.2963 12.5743 46.4934 12.1363 46.6519 11.6876C46.738 11.4439 46.5708 11.1816 46.3262 11.1816H45.2182C45.0797 11.1816 44.9562 11.2681 44.9 11.4026C44.8052 11.629 44.6979 11.85 44.5768 12.0683C43.9979 13.1162 43.213 13.951 42.2223 14.5715C41.2316 15.192 40.1497 15.5022 38.9768 15.5022C37.8039 15.5022 36.7208 15.192 35.7314 14.5715C34.7406 13.951 33.9558 13.1175 33.3768 12.0683C32.7978 11.0205 32.5083 9.88475 32.5083 8.65848C32.5083 7.43354 32.7978 6.29647 33.3768 5.24861C33.9558 4.20075 34.7406 3.36726 35.7314 2.74547C35.9497 2.60833 36.1743 2.48717 36.4014 2.38065C36.5287 2.32073 36.611 2.18759 36.611 2.0398V0.877432C36.611 0.664398 36.4476 0.504623 36.2617 0.504623C36.2217 0.505955 36.1806 0.512612 36.1406 0.528589ZM1.71445 9.55189H3.65724C4.48077 9.55189 5.14708 9.83416 5.65742 10.3987C6.16776 10.9632 6.42356 11.6396 6.42356 12.4292C6.42356 13.2187 6.16776 13.8964 5.65742 14.4596C5.14708 15.0242 4.48077 15.3065 3.65724 15.3065H1.71445V9.55189ZM1.71445 2.00651H3.65724C4.49574 2.00651 5.1658 2.28478 5.66865 2.84133C6.17151 3.39788 6.42356 4.07826 6.42356 4.88512C6.42356 5.69199 6.17151 6.36837 5.66865 6.9156C5.1658 7.46416 4.49449 7.73711 3.65724 7.73711H1.71445V2.00651ZM0.434227 0.193062C0.311944 0.193062 0.209627 0.237 0.126026 0.326208C0.0411767 0.415415 0 0.524595 0 0.652415V16.6606C0 16.7897 0.0424244 16.8989 0.126026 16.9868C0.209627 17.076 0.311944 17.1199 0.434227 17.1199H3.65724C4.46455 17.1199 5.21197 16.9109 5.897 16.4915C6.58327 16.072 7.12356 15.4995 7.52035 14.7752C7.9159 14.0496 8.1143 13.26 8.1143 12.4052C8.1143 11.6636 7.95832 10.9659 7.64513 10.3135C7.33319 9.66107 6.91019 9.1165 6.37614 8.68112C6.90894 8.22975 7.33194 7.6772 7.64513 7.02478C7.95708 6.37237 8.1143 5.67468 8.1143 4.93306C8.1143 4.06228 7.9159 3.26474 7.52035 2.53909C7.12356 1.81345 6.58327 1.24225 5.897 0.822842C5.21072 0.403432 4.46455 0.194393 3.65724 0.194393H0.434227V0.193062ZM16.1724 0.193062C16.0052 0.193062 15.883 0.282269 15.8068 0.459353L13.3612 6.57741L10.9156 0.459353C10.8394 0.282269 10.7172 0.193062 10.55 0.193062H9.63534C9.51306 0.193062 9.41448 0.237 9.33837 0.326208C9.26225 0.415415 9.22357 0.523264 9.22357 0.652415C9.22357 0.700348 9.23854 0.773578 9.26974 0.869443L12.5152 8.84888V16.6592C12.5152 16.7884 12.5576 16.8976 12.6412 16.9854C12.7248 17.0746 12.8272 17.1186 12.9494 17.1186H13.7954C13.9177 17.1186 14.02 17.0746 14.1036 16.9854C14.1872 16.8976 14.2297 16.7884 14.2297 16.6592V8.85021L17.4527 0.870774C17.4826 0.773578 17.4988 0.701679 17.4988 0.653747C17.4988 0.524595 17.4602 0.416747 17.384 0.327539C17.3079 0.239663 17.2081 0.194393 17.0871 0.194393H16.1724V0.193062ZM26.8384 0.193062C26.7162 0.193062 26.6138 0.237 26.5302 0.326208C26.4466 0.415415 26.4042 0.523264 26.4042 0.652415V7.71314H21.6951V0.652415C21.6951 0.523264 21.6527 0.415415 21.5691 0.326208C21.4855 0.238331 21.3819 0.193062 21.2609 0.193062H20.4149C20.2926 0.193062 20.1903 0.237 20.1067 0.326208C20.0231 0.415415 19.9807 0.523264 19.9807 0.652415V16.6606C19.9807 16.7897 20.0231 16.8989 20.1067 16.9868C20.1903 17.076 20.2926 17.1199 20.4149 17.1199H21.2609C21.3832 17.1199 21.4855 17.076 21.5691 16.9868C21.6527 16.8989 21.6951 16.7897 21.6951 16.6606V9.52659H26.4042V16.6592C26.4042 16.7884 26.4466 16.8976 26.5302 16.9854C26.6138 17.0746 26.7162 17.1186 26.8384 17.1186H27.6844C27.8067 17.1186 27.909 17.0746 27.9926 16.9854C28.0762 16.8976 28.1187 16.7884 28.1187 16.6592V0.652415C28.1187 0.523264 28.0762 0.415415 27.9926 0.326208C27.909 0.238331 27.8055 0.193062 27.6844 0.193062H26.8384V0.193062ZM56.5318 0.193062C56.4095 0.193062 56.3072 0.237 56.2236 0.326208C56.14 0.415415 56.0976 0.523264 56.0976 0.652415V12.9365C56.0976 13.6461 55.8605 14.2506 55.3888 14.7499C54.9159 15.2492 54.3445 15.4995 53.6744 15.4995C53.0043 15.4995 52.4329 15.2492 51.9599 14.7499C51.487 14.2506 51.2512 13.6461 51.2512 12.9365V0.652415C51.2512 0.523264 51.2088 0.415415 51.1252 0.326208C51.0416 0.238331 50.938 0.193062 50.817 0.193062H49.971C49.8487 0.193062 49.7464 0.237 49.6628 0.326208C49.5792 0.415415 49.5368 0.524595 49.5368 0.652415V12.9365C49.5368 13.726 49.7227 14.4557 50.097 15.124C50.4701 15.7938 50.973 16.325 51.6056 16.7205C52.2382 17.1159 52.927 17.313 53.6744 17.313C54.4206 17.313 55.1106 17.1159 55.7432 16.7205C56.3758 16.325 56.8787 15.7938 57.2518 15.124C57.6249 14.4557 57.812 13.726 57.812 12.9365V0.652415C57.812 0.523264 57.7696 0.415415 57.686 0.326208C57.6024 0.238331 57.4988 0.193062 57.3778 0.193062H56.5318V0.193062ZM62.8755 2.00651H64.6124C65.4509 2.00651 66.1509 2.30476 66.7149 2.90125C67.2789 3.49774 67.5609 4.23138 67.5609 5.10215C67.5609 5.95695 67.2789 6.68659 66.7149 7.29107C66.1509 7.89555 65.4497 8.1978 64.6124 8.1978H62.8755V2.00651ZM61.5953 0.193062C61.473 0.193062 61.3707 0.237 61.2871 0.326208C61.2035 0.415415 61.1611 0.523264 61.1611 0.652415V16.6606C61.1611 16.7897 61.2022 16.8989 61.2871 16.9868C61.3707 17.076 61.4743 17.1199 61.5953 17.1199H62.4413C62.5636 17.1199 62.6659 17.076 62.7495 16.9868C62.8331 16.8989 62.8755 16.7897 62.8755 16.6606V10.0112H64.8183L67.6071 16.8057C67.6982 17.0161 67.8354 17.1199 68.0188 17.1199H69.0932C69.3215 17.1199 69.4363 17.0227 69.4363 16.8296C69.4363 16.7498 69.4139 16.6526 69.3677 16.5394L66.3955 9.62379C67.249 9.25364 67.9415 8.65715 68.4755 7.83431C69.0083 7.0128 69.2754 6.10208 69.2754 5.10215C69.2754 4.19942 69.0695 3.37392 68.6577 2.62431C68.2459 1.87469 67.6869 1.2822 66.9782 0.846808C66.2695 0.411421 65.4809 0.194393 64.6124 0.194393H61.5953V0.193062ZM39.7642 1.86404C40.6376 1.97322 41.4574 2.26748 42.2223 2.74547C43.213 3.36593 43.9979 4.20075 44.5768 5.24861C45.0211 6.05281 45.2943 6.90894 45.3979 7.81833H39.7642V1.86404ZM38.5825 0.0119831C38.3617 0.0226348 38.187 0.218359 38.187 0.454028V1.86537C38.1882 1.86537 38.1895 1.86537 38.1895 1.86537V7.81833H38.187V9.07789C38.187 9.3109 38.3642 9.49863 38.5813 9.49863H45.3979V9.49996H46.7517C46.9651 9.49996 47.141 9.32155 47.151 9.09387C47.1573 8.95007 47.161 8.80361 47.161 8.65715C47.161 7.09402 46.7954 5.64672 46.0642 4.31659C45.333 2.98646 44.3385 1.93461 43.0808 1.16103C41.823 0.387455 40.4554 0 38.9781 0C38.8446 0.00133146 38.7123 0.00532583 38.5825 0.0119831ZM75.7276 0C74.9814 0 74.2914 0.193062 73.6588 0.580516C73.0262 0.967971 72.5233 1.48724 72.1502 2.13966C71.7771 2.79207 71.59 3.50573 71.59 4.27931C71.59 5.02093 71.746 5.6374 72.0579 6.12871C72.3698 6.62002 72.9763 7.27776 73.8747 8.09927L76.5037 10.4932C77.1126 11.0418 77.5394 11.4892 77.7839 11.8353C78.0273 12.1815 78.1495 12.581 78.1495 13.0323C78.1495 13.726 77.905 14.3105 77.4184 14.7859C76.9305 15.2612 76.3665 15.4995 75.7264 15.4995C75.0401 15.4995 74.4649 15.2665 74.0007 14.7978C73.5353 14.3305 73.3032 13.742 73.3032 13.0323V12.79C73.3032 12.6608 73.2608 12.5517 73.1772 12.4638C73.0936 12.3759 72.99 12.3306 72.869 12.3306H72.023C71.9007 12.3306 71.7984 12.3746 71.7148 12.4638C71.6312 12.553 71.5887 12.6608 71.5887 12.79V13.0323C71.5887 13.8059 71.7746 14.5196 72.149 15.172C72.5221 15.8244 73.0249 16.345 73.6575 16.7311C74.2902 17.1186 74.9789 17.3116 75.7264 17.3116C76.4725 17.3116 77.1626 17.1186 77.7952 16.7311C78.4278 16.3437 78.9307 15.8244 79.3037 15.172C79.6768 14.5196 79.864 13.8059 79.864 13.0323C79.864 12.4358 79.7841 11.9325 79.6244 11.5211C79.4647 11.1097 79.2239 10.7316 78.9045 10.3841C78.5838 10.0379 78.0822 9.54923 77.3959 8.92078L74.9265 6.67194C74.3164 6.1074 73.8946 5.66802 73.6575 5.3538C73.4217 5.03957 73.3032 4.68141 73.3032 4.27798C73.3032 3.58562 73.5465 3.00111 74.0344 2.52445C74.5223 2.04912 75.0863 1.81078 75.7264 1.81078C76.3964 1.81078 76.9679 2.05311 77.4408 2.53643C77.9125 3.01975 78.1495 3.60027 78.1495 4.27798V4.5203C78.1495 4.64946 78.1907 4.75864 78.2756 4.84651C78.3592 4.93572 78.4615 4.97966 78.5838 4.97966H79.4298C79.5521 4.97966 79.6544 4.93572 79.738 4.84651C79.8216 4.75864 79.864 4.64946 79.864 4.5203V4.27798C79.864 3.5044 79.6768 2.79074 79.3037 2.13832C78.9307 1.48591 78.4278 0.965308 77.7952 0.579185C77.1638 0.193062 76.4738 0 75.7276 0Z" fill="black"/>
          </svg>
        </NuxtLink>

        <!-- Inputs -->
        <div class="w-full px-3 xl:flex" v-show="(isScrolledInput && isHome) || !isHome">
          <SearchInput />
        </div>
      </div>

      <div class="gap-3 items-center justify-end hidden xl:flex" :class="{ 'w-7/12': isScrolledInput && isHome}">
        <UNavigationMenu
          color="neutral"
          v-model="active"
          content-orientation="vertical"
          :items="items"
          class="w-full justify-center"
          :ui="{ 
            link: 'text-xs text-toned py-2',
            content: 'w-fit',
            linkLeadingIcon: 'size-4',

          }"
        >
          <template #destinos-content="{ item }: { item: NavigationMenuItem }">
            <div class="p-5 w-92">
              <p class="text-sm font-bold mb-2 text-neutral-500 pl-2">{{ strapiContent?.labelDestinos.toUpperCase() }}</p>
              <ul class="grid lg:grid-cols-2">
                <li v-for="child in item?.children" :key="child.label">
                  <UButton
                    variant="link"
                    :to="child.to"
                    class="text-sm text-left rounded-md p-2 px-4 transition-colors hover:bg-elevated/50 w-full"
                    @click="active = ''"
                  >
                    <p class="font-medium text-toned">
                      {{ child.label }}
                    </p>
                  </UButton>
                </li>
              </ul>
              <UButton
                block
                variant="outline"
                class="mt-5"
                color="neutral"
                :to="getButtonUrl(strapiContent?.buttonDestinos)"
                target="_self"
                :aria-label="strapiContent?.buttonDestinos"
                size="lg"
                :label="strapiContent?.buttonDestinos?.label"
                @click="active = ''"
              />
            </div>
          </template>
          
        </UNavigationMenu>
        
        <!-- Idioma y moneda -->
        <UPopover>
          <UButton size="xs" aria-label="Idioma y moneda" variant="link" color="neutral" :label="`${selectedIdioma.toUpperCase()} | ${currency}`" />
          <template #content>
            <div class="flex flex-col gap-2 p-5 pt-3">
              <div>
                <p class="text-neutral-500 text-sm mb-1">{{ strapiContent?.tituloIdioma }}</p>
                <LocaleSelector size="md" />
              </div>
              <div>
                <p class="text-neutral-500 text-sm mb-1">{{ strapiContent?.tituloMoneda }}</p>
                <div>
                  <USelect :content="{ bodyLock: false }" class="w-full" size="md" v-model="currency" :items="dataMonedas?.data" labelKey="id" value-key="id" />
                </div>
              </div>
            </div>
          </template>
        </UPopover>

        <!-- Sesión -->
        <AuthState v-slot="{ user, loggedIn }">
          <!-- Logged in -->
          <UDropdownMenu v-if="loggedIn" :items="menuItems">
            <UUser
              :name="(user as OAuthUser)?.name || generateUsername((user as OAuthUser)?.email)"
              :avatar="{
                src: (user as OAuthUser)?.img,
                alt: `${(user as OAuthUser)?.name || ''} ${(user as OAuthUser)?.surname || ''}`,
                icon: !(user as OAuthUser)?.name ? 'i-lucide-user-circle' : undefined,
                ui: {
                  root: 'bg-linear-to-r from-primary-500 to-pink-500 text-[8px]',
                  fallback: 'text-white',
                  icon: 'text-white text-sm',
                }
              }"
              class="p-1 pr-2 border border-neutral-300 rounded-full hover:cursor-pointer bg-gray-100 shadow-xs"
              size="2xs"
            />
            <template #account="{ item }">
              <UUser
                :name="item.name"
                :description="item.label"
                :avatar="{
                  src: (user as OAuthUser)?.img,
                  alt: `${(user as OAuthUser)?.name || ''} ${(user as OAuthUser)?.surname || ''}`,
                  icon: !(user as OAuthUser)?.name ? 'i-lucide-user-circle' : undefined,
                  size: 'md',
                  ui: {
                    root: 'bg-linear-to-r from-primary-500 to-pink-500 text-xs',
                    fallback: 'text-white',
                    icon: 'text-white text-xl',
                  }
                }"
                size="sm"
                class="text-left truncate"
              />
            </template>
          </UDropdownMenu>

          <!-- Not logged -->
          <UButton
            v-else
            dynamic
            :to="`/${locale}/login`"
            aria-label="Usuario"
            icon="i-lucide-user-circle"
            :label="$t('buttons.login')"
            size="xs"
            variant="subtle"
            color="neutral"
          />
        </AuthState>
      </div>

      <!-- Menu plegado -->
      <div class="xl:hidden">
        <UModal fullscreen :transition="false" v-model:open="open">
          <UButton
            aria-label="Menu"
            icon="i-lucide-menu"
            color="neutral"
            variant="link"
          />
          <template #title>
            <div class="px-1">
              <NuxtLink aria-label="Home" :to="`/${locale}`">
                <svg width="75" height="18" viewBox="0 0 80 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M36.1406 0.528589C35.7064 0.699016 35.2846 0.909387 34.8741 1.16236C33.6164 1.93594 32.6219 2.9878 31.8907 4.31792C31.1595 5.64805 30.7939 7.09535 30.7939 8.65848C30.7939 10.2216 31.1595 11.6689 31.8907 12.999C32.6219 14.3292 33.6164 15.381 34.8741 16.1546C36.1306 16.9282 37.4982 17.3156 38.9768 17.3156C40.4554 17.3156 41.823 16.9282 43.0795 16.1546C44.336 15.381 45.3305 14.3292 46.063 12.999C46.2963 12.5743 46.4934 12.1363 46.6519 11.6876C46.738 11.4439 46.5708 11.1816 46.3262 11.1816H45.2182C45.0797 11.1816 44.9562 11.2681 44.9 11.4026C44.8052 11.629 44.6979 11.85 44.5768 12.0683C43.9979 13.1162 43.213 13.951 42.2223 14.5715C41.2316 15.192 40.1497 15.5022 38.9768 15.5022C37.8039 15.5022 36.7208 15.192 35.7314 14.5715C34.7406 13.951 33.9558 13.1175 33.3768 12.0683C32.7978 11.0205 32.5083 9.88475 32.5083 8.65848C32.5083 7.43354 32.7978 6.29647 33.3768 5.24861C33.9558 4.20075 34.7406 3.36726 35.7314 2.74547C35.9497 2.60833 36.1743 2.48717 36.4014 2.38065C36.5287 2.32073 36.611 2.18759 36.611 2.0398V0.877432C36.611 0.664398 36.4476 0.504623 36.2617 0.504623C36.2217 0.505955 36.1806 0.512612 36.1406 0.528589ZM1.71445 9.55189H3.65724C4.48077 9.55189 5.14708 9.83416 5.65742 10.3987C6.16776 10.9632 6.42356 11.6396 6.42356 12.4292C6.42356 13.2187 6.16776 13.8964 5.65742 14.4596C5.14708 15.0242 4.48077 15.3065 3.65724 15.3065H1.71445V9.55189ZM1.71445 2.00651H3.65724C4.49574 2.00651 5.1658 2.28478 5.66865 2.84133C6.17151 3.39788 6.42356 4.07826 6.42356 4.88512C6.42356 5.69199 6.17151 6.36837 5.66865 6.9156C5.1658 7.46416 4.49449 7.73711 3.65724 7.73711H1.71445V2.00651ZM0.434227 0.193062C0.311944 0.193062 0.209627 0.237 0.126026 0.326208C0.0411767 0.415415 0 0.524595 0 0.652415V16.6606C0 16.7897 0.0424244 16.8989 0.126026 16.9868C0.209627 17.076 0.311944 17.1199 0.434227 17.1199H3.65724C4.46455 17.1199 5.21197 16.9109 5.897 16.4915C6.58327 16.072 7.12356 15.4995 7.52035 14.7752C7.9159 14.0496 8.1143 13.26 8.1143 12.4052C8.1143 11.6636 7.95832 10.9659 7.64513 10.3135C7.33319 9.66107 6.91019 9.1165 6.37614 8.68112C6.90894 8.22975 7.33194 7.6772 7.64513 7.02478C7.95708 6.37237 8.1143 5.67468 8.1143 4.93306C8.1143 4.06228 7.9159 3.26474 7.52035 2.53909C7.12356 1.81345 6.58327 1.24225 5.897 0.822842C5.21072 0.403432 4.46455 0.194393 3.65724 0.194393H0.434227V0.193062ZM16.1724 0.193062C16.0052 0.193062 15.883 0.282269 15.8068 0.459353L13.3612 6.57741L10.9156 0.459353C10.8394 0.282269 10.7172 0.193062 10.55 0.193062H9.63534C9.51306 0.193062 9.41448 0.237 9.33837 0.326208C9.26225 0.415415 9.22357 0.523264 9.22357 0.652415C9.22357 0.700348 9.23854 0.773578 9.26974 0.869443L12.5152 8.84888V16.6592C12.5152 16.7884 12.5576 16.8976 12.6412 16.9854C12.7248 17.0746 12.8272 17.1186 12.9494 17.1186H13.7954C13.9177 17.1186 14.02 17.0746 14.1036 16.9854C14.1872 16.8976 14.2297 16.7884 14.2297 16.6592V8.85021L17.4527 0.870774C17.4826 0.773578 17.4988 0.701679 17.4988 0.653747C17.4988 0.524595 17.4602 0.416747 17.384 0.327539C17.3079 0.239663 17.2081 0.194393 17.0871 0.194393H16.1724V0.193062ZM26.8384 0.193062C26.7162 0.193062 26.6138 0.237 26.5302 0.326208C26.4466 0.415415 26.4042 0.523264 26.4042 0.652415V7.71314H21.6951V0.652415C21.6951 0.523264 21.6527 0.415415 21.5691 0.326208C21.4855 0.238331 21.3819 0.193062 21.2609 0.193062H20.4149C20.2926 0.193062 20.1903 0.237 20.1067 0.326208C20.0231 0.415415 19.9807 0.523264 19.9807 0.652415V16.6606C19.9807 16.7897 20.0231 16.8989 20.1067 16.9868C20.1903 17.076 20.2926 17.1199 20.4149 17.1199H21.2609C21.3832 17.1199 21.4855 17.076 21.5691 16.9868C21.6527 16.8989 21.6951 16.7897 21.6951 16.6606V9.52659H26.4042V16.6592C26.4042 16.7884 26.4466 16.8976 26.5302 16.9854C26.6138 17.0746 26.7162 17.1186 26.8384 17.1186H27.6844C27.8067 17.1186 27.909 17.0746 27.9926 16.9854C28.0762 16.8976 28.1187 16.7884 28.1187 16.6592V0.652415C28.1187 0.523264 28.0762 0.415415 27.9926 0.326208C27.909 0.238331 27.8055 0.193062 27.6844 0.193062H26.8384V0.193062ZM56.5318 0.193062C56.4095 0.193062 56.3072 0.237 56.2236 0.326208C56.14 0.415415 56.0976 0.523264 56.0976 0.652415V12.9365C56.0976 13.6461 55.8605 14.2506 55.3888 14.7499C54.9159 15.2492 54.3445 15.4995 53.6744 15.4995C53.0043 15.4995 52.4329 15.2492 51.9599 14.7499C51.487 14.2506 51.2512 13.6461 51.2512 12.9365V0.652415C51.2512 0.523264 51.2088 0.415415 51.1252 0.326208C51.0416 0.238331 50.938 0.193062 50.817 0.193062H49.971C49.8487 0.193062 49.7464 0.237 49.6628 0.326208C49.5792 0.415415 49.5368 0.524595 49.5368 0.652415V12.9365C49.5368 13.726 49.7227 14.4557 50.097 15.124C50.4701 15.7938 50.973 16.325 51.6056 16.7205C52.2382 17.1159 52.927 17.313 53.6744 17.313C54.4206 17.313 55.1106 17.1159 55.7432 16.7205C56.3758 16.325 56.8787 15.7938 57.2518 15.124C57.6249 14.4557 57.812 13.726 57.812 12.9365V0.652415C57.812 0.523264 57.7696 0.415415 57.686 0.326208C57.6024 0.238331 57.4988 0.193062 57.3778 0.193062H56.5318V0.193062ZM62.8755 2.00651H64.6124C65.4509 2.00651 66.1509 2.30476 66.7149 2.90125C67.2789 3.49774 67.5609 4.23138 67.5609 5.10215C67.5609 5.95695 67.2789 6.68659 66.7149 7.29107C66.1509 7.89555 65.4497 8.1978 64.6124 8.1978H62.8755V2.00651ZM61.5953 0.193062C61.473 0.193062 61.3707 0.237 61.2871 0.326208C61.2035 0.415415 61.1611 0.523264 61.1611 0.652415V16.6606C61.1611 16.7897 61.2022 16.8989 61.2871 16.9868C61.3707 17.076 61.4743 17.1199 61.5953 17.1199H62.4413C62.5636 17.1199 62.6659 17.076 62.7495 16.9868C62.8331 16.8989 62.8755 16.7897 62.8755 16.6606V10.0112H64.8183L67.6071 16.8057C67.6982 17.0161 67.8354 17.1199 68.0188 17.1199H69.0932C69.3215 17.1199 69.4363 17.0227 69.4363 16.8296C69.4363 16.7498 69.4139 16.6526 69.3677 16.5394L66.3955 9.62379C67.249 9.25364 67.9415 8.65715 68.4755 7.83431C69.0083 7.0128 69.2754 6.10208 69.2754 5.10215C69.2754 4.19942 69.0695 3.37392 68.6577 2.62431C68.2459 1.87469 67.6869 1.2822 66.9782 0.846808C66.2695 0.411421 65.4809 0.194393 64.6124 0.194393H61.5953V0.193062ZM39.7642 1.86404C40.6376 1.97322 41.4574 2.26748 42.2223 2.74547C43.213 3.36593 43.9979 4.20075 44.5768 5.24861C45.0211 6.05281 45.2943 6.90894 45.3979 7.81833H39.7642V1.86404ZM38.5825 0.0119831C38.3617 0.0226348 38.187 0.218359 38.187 0.454028V1.86537C38.1882 1.86537 38.1895 1.86537 38.1895 1.86537V7.81833H38.187V9.07789C38.187 9.3109 38.3642 9.49863 38.5813 9.49863H45.3979V9.49996H46.7517C46.9651 9.49996 47.141 9.32155 47.151 9.09387C47.1573 8.95007 47.161 8.80361 47.161 8.65715C47.161 7.09402 46.7954 5.64672 46.0642 4.31659C45.333 2.98646 44.3385 1.93461 43.0808 1.16103C41.823 0.387455 40.4554 0 38.9781 0C38.8446 0.00133146 38.7123 0.00532583 38.5825 0.0119831ZM75.7276 0C74.9814 0 74.2914 0.193062 73.6588 0.580516C73.0262 0.967971 72.5233 1.48724 72.1502 2.13966C71.7771 2.79207 71.59 3.50573 71.59 4.27931C71.59 5.02093 71.746 5.6374 72.0579 6.12871C72.3698 6.62002 72.9763 7.27776 73.8747 8.09927L76.5037 10.4932C77.1126 11.0418 77.5394 11.4892 77.7839 11.8353C78.0273 12.1815 78.1495 12.581 78.1495 13.0323C78.1495 13.726 77.905 14.3105 77.4184 14.7859C76.9305 15.2612 76.3665 15.4995 75.7264 15.4995C75.0401 15.4995 74.4649 15.2665 74.0007 14.7978C73.5353 14.3305 73.3032 13.742 73.3032 13.0323V12.79C73.3032 12.6608 73.2608 12.5517 73.1772 12.4638C73.0936 12.3759 72.99 12.3306 72.869 12.3306H72.023C71.9007 12.3306 71.7984 12.3746 71.7148 12.4638C71.6312 12.553 71.5887 12.6608 71.5887 12.79V13.0323C71.5887 13.8059 71.7746 14.5196 72.149 15.172C72.5221 15.8244 73.0249 16.345 73.6575 16.7311C74.2902 17.1186 74.9789 17.3116 75.7264 17.3116C76.4725 17.3116 77.1626 17.1186 77.7952 16.7311C78.4278 16.3437 78.9307 15.8244 79.3037 15.172C79.6768 14.5196 79.864 13.8059 79.864 13.0323C79.864 12.4358 79.7841 11.9325 79.6244 11.5211C79.4647 11.1097 79.2239 10.7316 78.9045 10.3841C78.5838 10.0379 78.0822 9.54923 77.3959 8.92078L74.9265 6.67194C74.3164 6.1074 73.8946 5.66802 73.6575 5.3538C73.4217 5.03957 73.3032 4.68141 73.3032 4.27798C73.3032 3.58562 73.5465 3.00111 74.0344 2.52445C74.5223 2.04912 75.0863 1.81078 75.7264 1.81078C76.3964 1.81078 76.9679 2.05311 77.4408 2.53643C77.9125 3.01975 78.1495 3.60027 78.1495 4.27798V4.5203C78.1495 4.64946 78.1907 4.75864 78.2756 4.84651C78.3592 4.93572 78.4615 4.97966 78.5838 4.97966H79.4298C79.5521 4.97966 79.6544 4.93572 79.738 4.84651C79.8216 4.75864 79.864 4.64946 79.864 4.5203V4.27798C79.864 3.5044 79.6768 2.79074 79.3037 2.13832C78.9307 1.48591 78.4278 0.965308 77.7952 0.579185C77.1638 0.193062 76.4738 0 75.7276 0Z" fill="black"/>
                </svg>
              </NuxtLink>
            </div>
          </template>
          <template #body>
            <div class="px-2">

              <UNavigationMenu
                color="neutral"
                orientation="vertical"
                content-orientation="vertical"
                :items="items"
                class="w-full justify-center"
                :ui="{ 
                  list: 'gap-2',
                  link: 'text-sm text-toned py-2',
                  content: 'w-fit' 
                }"
              >
                 <template #destinos-content="{ item }: { item: NavigationMenuItem }">
                  {{item}}
                 </template>
              </UNavigationMenu>

              <USeparator class="py-4" />

              <!-- Idioma y Moneda -->
              <div class="flex flex-col gap-2 pb-4">
                
                <!-- Idioma -->
                <div>
                  <p class="text-neutral-500 text-sm mb-1">{{ strapiContent?.tituloIdioma }}</p>                  
                  <LocaleSelector class="w-full" size="xl" />
                </div>

                <!-- Moneda -->
                <div>
                  <p class="text-neutral-500 text-sm mb-1">{{ strapiContent?.tituloMoneda }}</p>
                  <div>
                    <USelect class="w-full" size="xl" v-model="currency" :items="dataMonedas?.data" labelKey="id" value-key="id" />
                  </div>
                </div>
              </div>

              <USeparator class="py-2" />
              
              <!-- Sesión -->
              <AuthState v-slot="{ user, loggedIn, clear }">
                <div v-if="loggedIn" class="flex flex-col space-y-2 pt-1">
                  <div>
                    <UUser
                      :name="(user as OAuthUser)?.name || generateUsername((user as OAuthUser)?.email)"
                      :description="(user as OAuthUser)?.email"
                      :avatar="{
                        src: (user as OAuthUser)?.img,
                        alt: `${(user as OAuthUser)?.name || ''} ${(user as OAuthUser)?.surname || ''}`,
                        icon: !(user as OAuthUser)?.name ? 'i-lucide-user-circle' : undefined,
                        size: 'sm',
                        ui: {
                          root: 'bg-linear-to-r from-primary-500 to-pink-500 text-xs',
                          fallback: 'text-white',
                          icon: 'text-white text-xl',
                        }
                      }"
                      size="md"
                      class="py-2 px-3 pr-4"
                      @click="navigateTo(`/${locale}/user-zone/bookings`); open = false"
                    />
                  </div>
                  <UButton @click="clear(); open = false" :label="t('labels.login.dropdown.cerrar')" icon="i-lucide-log-out" variant="ghost" color="neutral" />
                </div>
                <UButton
                  v-else
                  dynamic
                  :to="`/${locale}/login`"
                  aria-label="Usuario"
                  icon="i-lucide-user-circle"
                  :label="$t('buttons.login')"
                  color="neutral"
                  class="mt-2"
                  size="lg"
                />
              </AuthState>

            </div>
          </template>

        </UModal>
      </div>
    </div>
  </header>
</template>